import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import styles from "../Home/HomePage.module.css";
import { useNavigate } from "react-router-dom";
import { apiFetch, auth } from "../../api";
import { useAuth } from "../context/AuthContext";

const ALLOWED_SUPPORTING_DOC_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];

const ALLOWED_SUPPORTING_DOC_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];

const isValidSupportingDoc = (file) => {
  if (!file) return false;
  const name = file.name.toLowerCase();
  const hasAllowedExtension = ALLOWED_SUPPORTING_DOC_EXTENSIONS.some((ext) =>
    name.endsWith(ext),
  );
  return (
    ALLOWED_SUPPORTING_DOC_TYPES.includes(file.type) && hasAllowedExtension
  );
};

// ─── Cloudflare Turnstile config ───────────────────────────────────────
const TURNSTILE_SITE_KEY = "0x4AAAAAAFhH5hv5sjWl6VCi";

export default function P4Auth({ mode = "login" }) {
  const { login } = useAuth();
  localStorage.setItem("navbar_state", "user");
  const navigate = useNavigate();
  const [tab, setTab] = useState(mode);

  const REQUIRE_DOC = true;

  if (!TURNSTILE_SITE_KEY) {
    // Fail loudly instead of silently rendering a blank widget
    console.error(
      "[Turnstile] REACT_APP_TURNSTILE_SITE_KEY is not set — " +
        "the CAPTCHA cannot render. Add it to your .env and restart the dev server.",
    );
  }

  // ─── Turnstile state ──────────────────────────────────────────────────
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileReady, setTurnstileReady] = useState(false); // ← STATE (was a ref)
  const turnstileWidgetId = useRef(null);
  const turnstileScriptInjected = useRef(false); // only guards double-injection
  const needsTurnstile =
    tab === "register" || tab === "login" || tab === "reset" || showTurnstile;

  // ─── Load Turnstile script once ───────────────────────────────────────
  useEffect(() => {
    if (turnstileScriptInjected.current) return;
    turnstileScriptInjected.current = true;

    // Set the global callback BEFORE injecting the script
    window.onTurnstileLoad = () => {
      setTurnstileReady(true); // ← triggers re-render so the widget can draw
    };

    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  // ─── Render the Turnstile widget ──────────────────────────────────────
  useEffect(() => {
    if (tab === "reset") return;
    if (!needsTurnstile) return;
    if (!turnstileReady || !window.turnstile) return;

    const container = document.getElementById("turnstile-widget");
    if (!container) return;

    if (turnstileWidgetId.current) {
      try {
        window.turnstile.remove(turnstileWidgetId.current);
      } catch (_) {}
      turnstileWidgetId.current = null;
    }
    container.innerHTML = "";

    turnstileWidgetId.current = window.turnstile.render(container, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: "light",
      callback: (token) => setTurnstileToken(token),
      "error-callback": (err) => {
        setTurnstileToken("");
        console.error("Turnstile error:", err);
      },
    });

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (_) {}
        turnstileWidgetId.current = null;
      }
    };
  }, [needsTurnstile, showTurnstile, tab, turnstileReady]);

  // ─── All existing validation helpers (unchanged) ─────────────────────
  const isEmail = (s) => {
    const v = (s || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const luhn = (digits) => {
    let sum = 0,
      dbl = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      let n = digits.charCodeAt(i) - 48;
      if (dbl) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      sum += n;
      dbl = !dbl;
    }
    return sum % 10 === 0;
  };

  const validYYMMDD = (yy, mm, dd) => {
    const y = Number(yy),
      m = Number(mm),
      d = Number(dd);
    if (m < 1 || m > 12) return false;
    if (d < 1 || d > 31) return false;
    const fullYear = 1900 + y;
    const dt = new Date(fullYear, m - 1, d);
    return (
      dt.getFullYear() === fullYear &&
      dt.getMonth() === m - 1 &&
      dt.getDate() === d
    );
  };

  const isValidSouthAfricanID = (s) => {
    const v = (s || "").trim();
    if (!/^\d{13}$/.test(v)) return false;
    if (!validYYMMDD(v.slice(0, 2), v.slice(2, 4), v.slice(4, 6))) return false;
    return luhn(v);
  };

  const isValidCompanyReg = (s) => {
    const v = (s || "").trim();
    return (
      /^\d{4}\/\d{6}\/\d{2}$/.test(v) ||
      /^K\d{4}\/\d{6}\/\d{2}$/i.test(v) ||
      /^(CK|CC)\d{2}\/\d{6}\/\d{2}$/i.test(v)
    );
  };

  const normalizePhoneNumber = (value) => {
    let raw = String(value || "").trim();
    if (!raw) return "";
    const explicitlyInternational = raw.startsWith("+") || raw.startsWith("00");
    if (explicitlyInternational) raw = raw.replace(/\(0\)/g, "");
    let digits = raw.replace(/\D/g, "");
    if (digits.startsWith("00")) digits = digits.slice(2);
    if (digits.startsWith("0")) digits = `27${digits.slice(1)}`;
    return digits ? `+${digits}` : "";
  };

  const isValidInternationalPhone = (value) => {
    const normalized = normalizePhoneNumber(value);
    const digits = normalized.replace(/\D/g, "");
    return /^[1-9]\d{6,14}$/.test(digits);
  };

  const isValidPassportNumber = (s) =>
    /^[A-Z0-9]{6,15}$/.test((s || "").trim().toUpperCase());

  const carryEmail = () =>
    (
      localStorage.getItem("userEmail") ||
      JSON.parse(localStorage.getItem("profile") || "{}").email ||
      ""
    ).trim();

  // ─── State ────────────────────────────────────────────────────────────
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [full, setFull] = useState("");
  const [mobile, setMobile] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [identifierType, setIdentifierType] = useState("SA_ID");
  const [supportingDoc, setSupportingDoc] = useState(null);
  const [whatsappSameAsMobile, setWhatsappSameAsMobile] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [addrStreet, setAddrStreet] = useState("");
  const [addrSuburb, setAddrSuburb] = useState("");
  const [addrPostal, setAddrPostal] = useState("");
  const [addrProvince, setAddrProvince] = useState("");
  const [addrCountry, setAddrCountry] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [showTcs, setShowTcs] = useState(false);
  const [tcsAccepted, setTcsAccepted] = useState(false);

  const TCS_PDF_URL =
    "/legal/p4power-consumer-portal-eula-and-service-terms.pdf";
  const TCS_ACCEPTANCE_TEXT =
    'By clicking "I Accept", I agree to be bound by the P4Power Consumer Portal End User Licence Agreement and Service Terms, including the electronic communication and information-processing provisions described in those terms.';

  useEffect(() => {
    if (tab !== "register") {
      setShowTcs(false);
      setTcsAccepted(false);
    }
  }, [tab]);

  const title = useMemo(
    () =>
      ({
        login: "Welcome back",
        register: "Create your account",
        reset: "Reset your password",
      })[tab],
    [tab],
  );

  const API = "https://simplix.co.za/api";

  // ─── Submit ───────────────────────────────────────────────────────────
  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setMsg("");

    try {
      if (tab === "login") {
        const token = turnstileToken;
        setTurnstileToken("");
        if (token && turnstileWidgetId.current) {
          window.turnstile.reset(turnstileWidgetId.current);
        }
        const profile = await login(email, password, token || undefined);
        if (!profile.acceptedTcs) {
          navigate("/profile", { replace: true });
          return;
        }
        navigate("/meters");
      } else if (tab === "register") {
        if (!tcsAccepted) {
          setShowTcs(true);
          return;
        }

        // Turnstile is mandatory for registration
        const regToken = turnstileToken;
        setTurnstileToken("");
        if (regToken && turnstileWidgetId.current) {
          window.turnstile.reset(turnstileWidgetId.current); // free the single-use token for a retry
        }
        if (!regToken) {
          setError("Please complete the security check before registering.");
          return;
        }
        if (!isEmail(email))
          throw new Error("Please enter a valid email address.");
        if (password !== confirm) throw new Error("Passwords do not match.");
        if (!isValidInternationalPhone(mobile))
          throw new Error(
            "Enter a valid phone number including the country code, for example +27 xx xxx xxxx.",
          );
        const finalWhatsappNumber = whatsappSameAsMobile
          ? mobile
          : whatsappNumber;
        if (!whatsappSameAsMobile && !isValidInternationalPhone(whatsappNumber))
          throw new Error(
            "Enter a valid WhatsApp number including the country code, for example +27 xx xxx xxxx.",
          );
        if (!idNumber.trim())
          throw new Error(
            "Please enter an SA ID number, passport number, or company registration number.",
          );
        const kind = identifierType;
        if (kind === "SA_ID" && !isValidSouthAfricanID(idNumber))
          throw new Error(
            "Invalid South African ID number (must be 13 digits with valid date and checksum).",
          );
        if (kind === "CIPC" && !isValidCompanyReg(idNumber))
          throw new Error(
            "Invalid Company Registration No (expected formats like 2014/123456/07, K2014/123456/07, CK14/123456/07).",
          );
        if (kind === "PASSPORT" && !isValidPassportNumber(idNumber))
          throw new Error(
            "Invalid passport number. Use 6 to 15 letters and numbers, without spaces.",
          );
        if (REQUIRE_DOC && !supportingDoc)
          throw new Error(
            "Please upload an ID, passport or company registration document.",
          );
        if (supportingDoc && !isValidSupportingDoc(supportingDoc))
          throw new Error(
            "Supporting document must be a PDF, JPG, JPEG or PNG file.",
          );

        const fd = new FormData();
        fd.append("email", email.trim().toLowerCase());
        fd.append("password", password);
        fd.append("full_name", full);
        fd.append("mobile_number", normalizePhoneNumber(mobile));
        fd.append("identifier_type", kind);
        fd.append(
          "id_number",
          kind === "CIPC"
            ? ""
            : kind === "PASSPORT"
              ? idNumber.trim().toUpperCase()
              : idNumber.trim(),
        );
        fd.append("company_reg_no", kind === "CIPC" ? idNumber.trim() : "");
        fd.append("address_street", addrStreet);
        fd.append("address_suburb", addrSuburb);
        fd.append("address_postal_code", addrPostal);
        fd.append("address_province", addrProvince);
        fd.append("address_country", addrCountry);
        fd.append("whatsapp_number", normalizePhoneNumber(finalWhatsappNumber));
        fd.append("tcs_accepted", tcsAccepted ? "true" : "false");
        fd.append("captcha_token", regToken);
        if (supportingDoc) fd.append("supporting_doc", supportingDoc);

        const r = await fetch(`${API}/register/full/`, {
          method: "POST",
          body: fd,
        });
        if (r.status === 409)
          throw new Error(
            "Email or ID/Company Reg No is already registered. Try logging in.",
          );
        if (!r.ok) {
          const t = await r.text().catch(() => "");
          throw new Error(t || "Could not register");
        }
        setMsg("Registration successful. You can log in now.");
        setTab("login");
        setTcsAccepted(false);
      } else if (tab === "reset") {
        if (!isEmail(email)) {
          setError("Please enter a valid email address.");
          return;
        }
        const r = await fetch(`${API}/profile/reset-password/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
        });
        if (!r.ok) {
          const t = await r.text().catch(() => "");
          throw new Error(t || "Could not send reset email.");
        }
        setMsg(
          "If that email exists, you'll receive a verification link. Check your inbox.",
        );
        // Do NOT navigate away. Stay on this page.
      }
    } catch (err) {
      if (err.captchaRequired) {
        setShowTurnstile(true);
        setError("Please complete the security check and try again.");
        return;
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false); // ← re-enable when done
    }
  }

  return (
    <div className={styles.authPage}>
      <div
        className={`${styles.authShell} ${tab === "login" ? styles.authShellLogin : ""}`}
      >
        <section className={styles.authIntro}>
          <span className={styles.kicker}>P4Power Portal</span>
          <h1 className={styles.authHeroTitle}>{title}</h1>
          <p className={styles.authHeroText}>
            Secure access to your prepaid electricity account, registrations,
            meter information, and support tools.
          </p>
          <div className={styles.authFeatureGrid}>
            <div className={styles.authFeatureCard}>
              <span>01</span>
              <strong>Secure login</strong>
              <p>Access your portal with protected account credentials.</p>
            </div>
            <div className={styles.authFeatureCard}>
              <span>02</span>
              <strong>Easy registration</strong>
              <p>Create a profile with verified account information.</p>
            </div>
          </div>
        </section>

        <section className={styles.authCard}>
          <div className={styles.authHeader}>
            <p className={styles.sectionEyebrow}>Account access</p>
            <h2>{title}</h2>
            <p>P4POWER PORTAL</p>
          </div>

          {showTcs && (
            <div
              className={`${styles.modalBackdrop} ${styles.tcsModalBackdrop}`}
            >
              <div
                className={`${styles.authModal} ${styles.tcsScrollableModal}`}
              >
                <h2>Terms &amp; Conditions</h2>
                <p>
                  You may download the Terms &amp; Conditions below. Downloading
                  the document is optional.
                </p>
                <a className={styles.tcsPdfLink} href={TCS_PDF_URL} download>
                  Download Terms &amp; Conditions (PDF)
                </a>
                <div className={styles.tcsConfirmation}>
                  <span>{TCS_ACCEPTANCE_TEXT}</span>
                </div>
                <div className={styles.modalActions}>
                  <button
                    type="button"
                    className={styles.navBtnGhostDark}
                    onClick={() => {
                      setShowTcs(false);
                      setTcsAccepted(false);
                    }}
                  >
                    I Decline
                  </button>
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={() => {
                      setTcsAccepted(true);
                      setShowTcs(false);
                    }}
                  >
                    I Accept
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={styles.authTabs}>
            {tab !== "login" && (
              <button
                type="button"
                className={styles.authTabBtn}
                onClick={() => setTab("login")}
              >
                Login
              </button>
            )}
            {tab !== "register" && (
              <button
                type="button"
                className={styles.authTabBtn}
                onClick={() => setTab("register")}
              >
                Register
              </button>
            )}
            {tab !== "reset" && (
              <button
                type="button"
                className={styles.authTabBtn}
                onClick={() => navigate("/forgot-password")}
              >
                Reset Password
              </button>
            )}
          </div>

          <form className={styles.authForm} onSubmit={onSubmit}>
            {tab !== "reset" && (
              <>
                <input
                  className={styles.authInput}
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <input
                  className={styles.authInput}
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </>
            )}

            {/* ★ Turnstile widget — appears only when backend demands it */}
            {tab !== "reset" && needsTurnstile && (
              <div
                id="turnstile-widget"
                style={{ margin: "12px 0", minHeight: "88px" }}
              />
            )}

            {tab === "register" && (
              <>
                <input
                  className={styles.authInput}
                  placeholder="Full Name"
                  value={full}
                  onChange={(e) => setFull(e.target.value)}
                  required
                />
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  className={styles.authInput}
                  placeholder="Mobile number, e.g. +27 xx xxx xxxx"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  required
                />
                <div className={styles.fileBlock}>
                  <label
                    style={{ display: "flex", gap: 10, alignItems: "center" }}
                  >
                    <input
                      type="checkbox"
                      checked={whatsappSameAsMobile}
                      onChange={(e) =>
                        setWhatsappSameAsMobile(e.target.checked)
                      }
                    />
                    WhatsApp number is the same as mobile number
                  </label>
                </div>
                {!whatsappSameAsMobile && (
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    className={styles.authInput}
                    placeholder="WhatsApp number (0XXXXXXXXX or +27XXXXXXXXX)"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    required
                  />
                )}
                <select
                  className={styles.authInput}
                  value={identifierType}
                  onChange={(e) => setIdentifierType(e.target.value)}
                >
                  <option value="SA_ID">South African ID</option>
                  <option value="PASSPORT">Passport number</option>
                  <option value="CIPC">Company registration number</option>
                </select>
                <input
                  className={styles.authInput}
                  placeholder={
                    identifierType === "SA_ID"
                      ? "South African ID number"
                      : identifierType === "PASSPORT"
                        ? "Passport number"
                        : "Company Reg No (e.g. 2014/123456/07)"
                  }
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  required
                />
                <div className={styles.authRow}>
                  <input
                    className={styles.authInput}
                    placeholder="Street"
                    value={addrStreet}
                    onChange={(e) => setAddrStreet(e.target.value)}
                    required
                  />
                  <input
                    className={styles.authInput}
                    placeholder="Suburb"
                    value={addrSuburb}
                    onChange={(e) => setAddrSuburb(e.target.value)}
                    required
                  />
                </div>
                <div className={styles.authRow}>
                  <input
                    className={styles.authInput}
                    placeholder="Postal code"
                    value={addrPostal}
                    onChange={(e) => setAddrPostal(e.target.value)}
                    required
                  />
                  <input
                    className={styles.authInput}
                    placeholder="Province"
                    value={addrProvince}
                    onChange={(e) => setAddrProvince(e.target.value)}
                    required
                  />
                </div>
                <input
                  className={styles.authInput}
                  placeholder="Country"
                  value={addrCountry}
                  onChange={(e) => setAddrCountry(e.target.value)}
                  required
                />
                <div className={styles.fileBlock}>
                  <input
                    className={styles.authInput}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                    onChange={(e) =>
                      setSupportingDoc(e.target.files?.[0] || null)
                    }
                    {...(REQUIRE_DOC ? { required: true } : {})}
                  />
                  <p>
                    Upload <strong>one file</strong>: PDF, JPG, JPEG or PNG.{" "}
                    <em>SA ID or passport for individuals</em> or{" "}
                    <em>Proof of Company Registration for companies</em>.
                  </p>
                </div>
                <div className={styles.tcsAcceptanceRow}>
                  <button
                    type="button"
                    className={styles.navBtnGhostDark}
                    onClick={() => setShowTcs(true)}
                  >
                    Review or download Terms &amp; Conditions
                  </button>
                  <span
                    className={
                      tcsAccepted ? styles.tcsAccepted : styles.tcsRequired
                    }
                  >
                    {tcsAccepted ? "Accepted" : "Acceptance required"}
                  </span>
                </div>
                <input
                  className={styles.authInput}
                  type="password"
                  placeholder="Confirm password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </>
            )}

            {tab === "reset" && (
              <>
                <input
                  className={styles.authInput}
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className={styles.authNote}>
                  You'll receive a password reset link if the email exists.
                </p>
              </>
            )}

            {error && <div className={styles.authError}>{error}</div>}
            {msg && <div className={styles.authSuccess}>{msg}</div>}

            <div className={styles.authHelp}>
              <span>Need help? info@p4power.co.za</span>
              <button
                className={styles.primaryBtn}
                type="submit"
                disabled={submitting || (tab === "register" && !tcsAccepted)}
              >
                {submitting
                  ? tab === "register"
                    ? "Creating…"
                    : tab === "login"
                      ? "Logging in…"
                      : "Sending…"
                  : tab === "login"
                    ? "Login"
                    : tab === "register"
                      ? "Create account"
                      : "Send reset"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
